import { useState, useContext } from 'react';
import { toast } from 'react-toastify';
import { AuthContext } from '../../context/AuthProvider';

import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  Box,
  CircularProgress,
} from '@mui/material';

import proposalSampleServices from '../../services/proposalSampleServices';

/**
 * Dialog for saving current proposal as a sample/template
 */
const SaveSampleDialog = ({ open, onClose, proposal }) => {
  const { userData } = useContext(AuthContext);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isShared, setIsShared] = useState(false);
  const [saving, setSaving] = useState(false);

  // Only superadmin and admin can share samples
  const canShare = userData?.role === 'superadmin' || userData?.role === 'admin';

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error('Please enter a sample name');
      return;
    }

    setSaving(true);
    try {
      // Extract data from proposal (excluding equipment models)
      const sampleData = {
        name: name.trim(),
        description: description.trim() || null,
        isShared,
        sampleType: proposal.proposalType || null,
        // Common fields
        guarantees: proposal.guarantees || [],
        paymentNotes: proposal.paymentNotes || null,
        discounts: proposal.discounts || [],
        contractRebates: proposal.contractRebates || [],
        // Installation-specific fields
        jobOverview: proposal.jobOverview || null,
        scopeOfWorks: proposal.scopeOfWorks || [],
        warranties: proposal.warranties || [],
        installationPrice: Number(proposal.installationPrice) || 0,
        // Service-specific fields
        equipmentOverview: proposal.equipmentOverview || null,
        workDescriptions: proposal.workDescriptions || [],
        jobPrice: Number(proposal.jobPrice) || 0,
        // Save system templates (names only, no equipment)
        systemTemplates: (proposal.proposalSystems || []).map(s => ({
          name: s.name,
          notes: s.notes || null,
        })),
      };

      const response = await proposalSampleServices.create(sampleData);
      
      if (response.status === 200) {
        toast.success('Sample saved successfully!');
        handleClose();
      }
    } catch (error) {
      console.error('Error saving sample:', error);
      toast.error('Failed to save sample');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setName('');
    setDescription('');
    setIsShared(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Save Proposal as Sample</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Sample Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            required
            autoFocus
            placeholder="e.g., Standard AC Replacement"
          />
          
          <TextField
            label="Description (optional)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            rows={2}
            placeholder="Brief description of this template..."
          />

          {canShare && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={isShared}
                  onChange={(e) => setIsShared(e.target.checked)}
                />
              }
              label="Share with all team members"
            />
          )}

          <Box sx={{ backgroundColor: '#f5f5f5', p: 2, borderRadius: 1 }}>
            <Box sx={{ fontSize: 13, color: '#666' }}>
              <strong>This sample will include:</strong>
              <ul style={{ margin: '8px 0 0 0', paddingLeft: 20 }}>
                {/* Installation fields */}
                {proposal.jobOverview && <li>Job Overview text</li>}
                {proposal.scopeOfWorks?.length > 0 && <li>{proposal.scopeOfWorks.length} Scope of Work items</li>}
                {proposal.warranties?.length > 0 && <li>{proposal.warranties.length} Warranty items</li>}
                {Number(proposal.installationPrice) > 0 && <li>Installation Price: ${Number(proposal.installationPrice).toFixed(2)}</li>}
                {/* Service fields */}
                {proposal.equipmentOverview && <li>Equipment Overview text</li>}
                {proposal.workDescriptions?.length > 0 && <li>{proposal.workDescriptions.length} Work Description items</li>}
                {Number(proposal.jobPrice) > 0 && <li>Job Price: ${Number(proposal.jobPrice).toFixed(2)}</li>}
                {/* Common fields */}
                {proposal.discounts?.length > 0 && <li>{proposal.discounts.length} Discounts</li>}
                {proposal.contractRebates?.length > 0 && <li>{proposal.contractRebates.length} Rebates</li>}
                {proposal.guarantees?.length > 0 && <li>{proposal.guarantees.length} Guarantees</li>}
                {proposal.proposalSystems?.length > 0 && <li>{proposal.proposalSystems.length} System names (without equipment models)</li>}
              </ul>
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>
          Cancel
        </Button>
        <Button 
          onClick={handleSave} 
          variant="contained" 
          disabled={saving || !name.trim()}
          startIcon={saving && <CircularProgress size={20} />}
        >
          {saving ? 'Saving...' : 'Save Sample'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SaveSampleDialog;

